import React, { useEffect, useState, useRef } from 'react';
import { SendIcon, SparklesIcon } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import { RestaurantCard } from './RestaurantCard';
import { motion, AnimatePresence } from 'framer-motion';
// Define the script sequence
const SCRIPT = [{
  id: 'msg-1',
  role: 'user' as const,
  delay: 1000,
  content: <p data-id="element-1">Which restaurants near downtown have the best health ratings?</p>
}, {
  id: 'msg-2',
  role: 'ai' as const,
  delay: 2500,
  content: <div className="space-y-3" data-id="element-2">
        <p data-id="element-3">
          Great question! Here are the top-rated restaurants near downtown
          Springfield, all with excellent inspection scores:
        </p>
        <div className="flex flex-col gap-1" data-id="element-4">
          <RestaurantCard name="The Rustic Table" cuisine="American" address="124 Main St, Springfield" grade="A" gradeText="Excellent" date="Oct 14, 2023" data-id="element-5" />
          <RestaurantCard name="El Camino Taqueria" cuisine="Mexican" address="78 Jefferson Ave, Springfield" grade="A" gradeText="Excellent" date="Sep 22, 2023" data-id="element-6" />
        </div>
        <p data-id="element-7">
          Both have maintained A ratings for 3+ consecutive inspections. Would
          you like details on either one?
        </p>
      </div>
}, {
  id: 'msg-3',
  role: 'user' as const,
  delay: 5000,
  content: <p data-id="element-8">What about Bella Napoli Pizzeria? Is it safe?</p>
}, {
  id: 'msg-4',
  role: 'ai' as const,
  delay: 6500,
  content: <div className="space-y-3" data-id="element-9">
        <p data-id="element-10">Bella Napoli Pizzeria holds a B - Good rating.</p>
        <RestaurantCard name="Bella Napoli Pizzeria" cuisine="Italian" address="890 Oak Ave, Springfield" grade="B" gradeText="Good" date="Nov 1, 2023" issuesCount={2} issuesText="2 minor issues" data-id="element-11" />
        <div className="space-y-2" data-id="element-12">
          <p data-id="element-13">The 2 issues found were:</p>
          <ul className="list-disc pl-5 space-y-1 text-slate-700" data-id="element-14">
            <li data-id="element-15">
              Improper food storage temperature in walk-in cooler (corrected
              on-site)
            </li>
            <li data-id="element-16">Missing date labels on 3 prep containers</li>
          </ul>
          <p data-id="element-17">
            Both were minor and corrected during the inspection. The restaurant
            has no history of critical violations. Overall, it is safe to eat
            at.
          </p>
        </div>
      </div>
}, {
  id: 'msg-5',
  role: 'user' as const,
  delay: 9000,
  content: <p data-id="element-18">Are there any restaurants I should avoid?</p>
}, {
  id: 'msg-6',
  role: 'ai' as const,
  delay: 10500,
  content: <div className="space-y-3" data-id="element-19">
        <p data-id="element-20">
          Based on recent inspections, one restaurant has significant concerns:
        </p>
        <RestaurantCard name="Golden Dragon" cuisine="Chinese" address="455 Riverside Dr, Springfield" grade="C" gradeText="Needs Improvement" date="Nov 30, 2023" issuesCount={3} issuesText="3 issues" data-id="element-21" />
        <p data-id="element-22">
          Golden Dragon had 3 violations including a critical temperature
          control issue. I would recommend checking back after their follow-up
          inspection scheduled for January 2024.
        </p>
      </div>
}];
export function ChatDemo() {
  const [visibleMessages, setVisibleMessages] = useState<typeof SCRIPT>([]);
  const [isFinished, setIsFinished] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [visibleMessages]);
  useEffect(() => {
    // Reset state on mount
    setVisibleMessages([]);
    setIsFinished(false);
    const timeouts = SCRIPT.map((msg, index) => {
      return setTimeout(() => {
        setVisibleMessages(prev => [...prev, msg]);
        if (index === SCRIPT.length - 1) {
          setTimeout(() => setIsFinished(true), 1500);
        }
      }, msg.delay);
    });
    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, []);
  const handleRestart = () => {
    setVisibleMessages([]);
    setIsFinished(false);
    // Re-trigger the effect by forcing a small delay, or just copy the logic above
    // For simplicity in this demo, we'll just reload the component state manually
    const timeouts = SCRIPT.map((msg, index) => {
      return setTimeout(() => {
        setVisibleMessages(prev => [...prev, msg]);
        if (index === SCRIPT.length - 1) {
          setTimeout(() => setIsFinished(true), 1500);
        }
      }, msg.delay);
    });
  };
  return <div className="flex flex-col w-full max-w-2xl mx-auto bg-slate-50 rounded-2xl shadow-2xl overflow-hidden border border-slate-200 h-[600px]" data-id="element-23">
      {/* Chat Header */}
      <div className="bg-slate-900 px-6 py-4 flex items-center justify-between border-b border-slate-800" data-id="element-24">
        <div className="flex items-center space-x-3" data-id="element-25">
          <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center border border-slate-700" data-id="element-26">
            <SparklesIcon className="w-5 h-5 text-emerald-400" data-id="element-27" />
          </div>
          <div data-id="element-28">
            <h3 className="text-white font-semibold flex items-center" data-id="element-29">
              Franklin County Health AI
            </h3>
            <div className="flex items-center text-xs text-slate-400 mt-0.5" data-id="element-30">
              <span className="w-2 h-2 rounded-full bg-emerald-500 mr-1.5 animate-pulse" data-id="element-31"></span>
              Online & ready to help
            </div>
          </div>
        </div>
      </div>

      {/* Chat Messages Area */}
      <div className="flex-1 overflow-y-auto p-6 scroll-smooth" data-id="element-32">
        {visibleMessages.length === 0 && <div className="h-full flex items-center justify-center text-slate-400 text-sm" data-id="element-33">
            Connecting to public health database...
          </div>}

        <AnimatePresence data-id="element-34">
          {visibleMessages.map(msg => <ChatMessage key={msg.id} role={msg.role} data-id="element-35">
              {msg.content}
            </ChatMessage>)}
        </AnimatePresence>

        <div ref={messagesEndRef} data-id="element-36" />
      </div>

      {/* Chat Input Area (Fake) */}
      <div className="p-4 bg-white border-t border-slate-200" data-id="element-37">
        <div className="relative flex items-center" data-id="element-38">
          <input type="text" placeholder="Ask about any restaurant..." className="w-full bg-slate-100 border-transparent focus:border-emerald-500 focus:bg-white focus:ring-0 rounded-full py-3 pl-5 pr-12 text-sm text-slate-900 placeholder-slate-400 transition-colors" data-id="element-39" />
          <button className="absolute right-2 p-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors" data-id="element-40">
            <SendIcon className="w-4 h-4" data-id="element-41" />
          </button>
        </div>
      </div>
    </div>;
}